import { inject, injectable } from 'tsyringe';
import { LicenseStatus } from '@bmai/licenses-api-client';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { LicensesApiService } from '@/features/licenses/LicensesApiService';
import { GlobalFilterValueService } from '@/features/global-filters/GlobalFilterValueService';
import { GlobalFilterController } from '@/features/global-filters/GlobalFilterController';
import { isSeatLicensedProviderType } from '@/shared/ProviderPlatform';
import { isSubscriptionCost, recordSeatCount } from '@/features/licenses/CostRecordDimensions';
import { formatCount, formatUsd, humanUser } from '@/shared/analytics/MetricFormatting';
import type { TopicCallout } from '@/shared/surfaces/TopicNarration';
import { computeLicenseFit, normalizeUserId, type LicenseFitConnection, type LicenseFitSeat, type LicenseFitUsageCell } from './LicenseFitMath';
import type { LicenseFitResult } from './LicenseFitTypes';

/** Monthly delta below which a footing difference is treated as noise (mirrors the recommendation threshold). */
const MIN_DELTA = 10;
/**
 * Minimum believable per-seat monthly price. Per-seat cost is derived from subscription cost records, so a
 * connection with little/no recorded subscription yields a near-zero price that would fabricate "should
 * license" savings. Anything below this is treated as unknown (a real AI seat costs at least a few $/mo).
 */
const MIN_CREDIBLE_SEAT_MONTHLY = 1;

/** Working-day run-rate multiplier projecting month-to-date usage to the full month: total weekdays ÷ weekdays elapsed (through today). */
function monthProjectionRatio(now: Date): number {
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth();
    const today = now.getUTCDate();
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    let total = 0;
    let elapsed = 0;
    for (let day = 1; day <= lastDay; day += 1) {
        const weekday = new Date(Date.UTC(year, month, day)).getUTCDay();
        if (weekday < 1 || weekday > 5) continue;
        total += 1;
        if (day <= today) elapsed += 1;
    }
    return elapsed > 0 ? total / elapsed : 1;
}

/**
 * Per-user footing efficiency (License Fit): for each user, compares the cost of their current billing model
 * (seat license vs metered/API) against the alternative, over the trailing 30 days. Seat rows come from active
 * assignments (so zero-usage wasted seats surface); metered rows come from usage on connections without a seat
 * pool. Metered-equivalent usage is priced at the empirical per-model fleet rate; the metered→seat side benchmarks
 * against the cheapest available seat. The global provider/user filters fold into the usage aggregate via the
 * gateway and are applied to seat rows here; the date filter does not apply (this is a fixed monthly-basis view).
 */
@injectable()
export class LicenseFitViewModel extends BaseViewModel {
    loading = false;
    error: string | null = null;
    result: LicenseFitResult | null = null;
    /** Top footing opportunities, formatted for the rail Insights card. */
    callouts: TopicCallout[] = [];

    constructor(
        @inject(IAnalyticsGatewayToken) private readonly gateway: IAnalyticsGateway,
        @inject(LicensesApiService) private readonly licenses: LicensesApiService,
        @inject(GlobalFilterValueService) private readonly providers: GlobalFilterValueService,
        @inject(GlobalFilterController) private readonly filters: GlobalFilterController,
    ) {
        super();
    }

    async reload(): Promise<void> {
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            const end = new Date();
            const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
            const startIso = start.toISOString();
            const endIso = end.toISOString();
            const projectionRatio = monthProjectionRatio(end);

            const [usageAgg, rateAgg, pools, records, assignments, platformOverheadByUser] = await Promise.all([
                this.gateway.aggregate({ source: 'interactions', groupBy: ['user_id', 'provider_connection_id', 'model'], metrics: ['allocatedCostUsd', 'totalTokens'], start: startIso, end: endIso }),
                this.gateway.aggregate({ source: 'interactions', groupBy: ['model'], metrics: ['allocatedCostUsd', 'totalTokens'], start: startIso, end: endIso }),
                this.licenses.listPools().catch((): never[] => []),
                this.licenses.getCostRecords(startIso, endIso).catch((): never[] => []),
                this.licenses.listAllAssignments().catch((): never[] => []),
                this.fetchPlatformOverhead(startIso),
            ]);
            await this.providers.loadProviders();

            const usage: LicenseFitUsageCell[] = usageAgg.rows
                .map((row) => ({
                    userId: row.keys.user_id ?? '',
                    connectionId: row.keys.provider_connection_id ?? '',
                    model: row.keys.model ?? '',
                    allocatedCostUsd: row.metrics.allocatedCostUsd ?? 0,
                    totalTokens: row.metrics.totalTokens ?? 0,
                }))
                .filter((cell) => cell.userId.length > 0 && cell.userId !== '—' && cell.connectionId.length > 0);

            const rateByModel = new Map<string, number>();
            for (const row of rateAgg.rows) {
                const model = row.keys.model;
                if (model === undefined || model === '—') continue;
                const tokens = row.metrics.totalTokens ?? 0;
                const cost = row.metrics.allocatedCostUsd ?? 0;
                if (tokens > 0 && cost > 0) rateByModel.set(model, (cost / tokens) * 1_000_000);
            }

            const currency = records.find((record) => isSubscriptionCost(record) && record.currency.length > 0)?.currency ?? records.find((record) => record.currency.length > 0)?.currency ?? 'USD';
            const subscriptionByConnection = new Map<string, { amount: number; seats: number | null }>();
            for (const record of records) {
                if (!isSubscriptionCost(record) || record.currency !== currency) continue;
                const connectionId = record.providerConnectionId;
                if (!connectionId) continue;
                const entry = subscriptionByConnection.get(connectionId) ?? { amount: 0, seats: null };
                entry.amount += record.amount;
                const seats = recordSeatCount(record);
                if (seats !== null && seats > 0) entry.seats = seats;
                subscriptionByConnection.set(connectionId, entry);
            }

            const connections = new Map<string, LicenseFitConnection>();
            for (const pool of pools) {
                const seatLicensed = isSeatLicensedProviderType(pool.providerTypeId);
                const subscription = subscriptionByConnection.get(pool.providerConnectionId);
                const seatDivisor = subscription?.seats ?? (pool.assignedSeats > 0 ? pool.assignedSeats : null);
                const rawPerSeat = seatLicensed && subscription !== undefined && subscription.amount > 0 && seatDivisor !== null ? subscription.amount / seatDivisor : null;
                const perSeatMonthly = rawPerSeat !== null && rawPerSeat >= MIN_CREDIBLE_SEAT_MONTHLY ? rawPerSeat : null;
                connections.set(pool.providerConnectionId, {
                    provider: pool.providerDisplayName.length > 0 ? pool.providerDisplayName : this.providers.providerLabel(pool.providerConnectionId),
                    seatLicensed,
                    perSeatMonthly,
                });
            }
            for (const cell of usage) {
                if (!connections.has(cell.connectionId)) {
                    connections.set(cell.connectionId, { provider: this.providers.providerLabel(cell.connectionId), seatLicensed: false, perSeatMonthly: null });
                }
            }

            let cheapestSeatMonthly: number | null = null;
            for (const connection of connections.values()) {
                if (!connection.seatLicensed || connection.perSeatMonthly === null || connection.perSeatMonthly <= 0) continue;
                cheapestSeatMonthly = cheapestSeatMonthly === null ? connection.perSeatMonthly : Math.min(cheapestSeatMonthly, connection.perSeatMonthly);
            }

            const connectionFilter = this.activeValues('provider_connection_id');
            const userFilter = this.activeValues('user_id');
            const seats: LicenseFitSeat[] = assignments
                .filter((assignment) => assignment.status === LicenseStatus.Active)
                .filter((assignment) => connectionFilter === null || connectionFilter.has(assignment.providerConnectionId))
                .filter((assignment) => userFilter === null || userFilter.has(normalizeUserId(assignment.userId)))
                .map((assignment) => ({ userId: assignment.userId, connectionId: assignment.providerConnectionId }));

            this.result = computeLicenseFit({
                usage,
                seats,
                connections,
                rateByModel,
                userLabel: humanUser,
                currency,
                cheapestSeatMonthly,
                projectionRatio,
                minDelta: MIN_DELTA,
                platformOverheadByUser,
            });
            this.callouts = this.buildCallouts();
        } catch (cause) {
            this.error = cause instanceof Error ? cause.message : String(cause);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    /**
     * Per-user platform (classifier/topic) overhead for the current month, read from the telemetry-owned
     * `platform_costs` dataset via the raw OLAP path (the typed gateway is scoped to `interactions`). Tenant is
     * resolved server-side, so the SQL carries no tenant predicate. Best-effort: a query failure (e.g. the
     * dataset has no objects yet) yields an empty map — the overhead column simply shows nothing.
     */
    private async fetchPlatformOverhead(startIso: string): Promise<Map<string, number>> {
        const overhead = new Map<string, number>();
        try {
            const table = await this.gateway.rawQuery(
                `select user_id, sum(amount) as overhead
                 from platform_costs
                 where cast(period_start as timestamp) >= cast('${startIso}' as timestamp)
                 group by user_id`,
            );
            for (const row of table.rows) {
                const userId = typeof row.user_id === 'string' ? row.user_id : String(row.user_id ?? '');
                const amount = typeof row.overhead === 'number' ? row.overhead : Number(row.overhead ?? 0);
                if (userId.length === 0 || !Number.isFinite(amount) || amount <= 0) continue;
                const key = normalizeUserId(userId);
                overhead.set(key, (overhead.get(key) ?? 0) + amount);
            }
        } catch (error) {
            void error;
        }
        return overhead;
    }

    /** Summarizes the biggest footing opportunities as rail callouts. */
    private buildCallouts(): TopicCallout[] {
        if (this.result === null) return [];
        const { summary, rows } = this.result;
        const callouts: TopicCallout[] = [];
        if (summary.reclaimCandidates > 0 && summary.wastedSeatUsd > 0) {
            callouts.push({ tone: 'opportunity', text: `Reclaim ${formatCount(summary.reclaimCandidates)} underused seat${summary.reclaimCandidates === 1 ? '' : 's'} to save ~${formatUsd(summary.wastedSeatUsd)}/mo.` });
        }
        const shouldLicense = rows.filter((row) => row.verdict === 'should-license').length;
        if (shouldLicense > 0 && summary.apiToSeatUsd > 0) {
            callouts.push({ tone: 'opportunity', text: `${formatCount(shouldLicense)} heavy metered user${shouldLicense === 1 ? '' : 's'} would be cheaper on a seat — save ~${formatUsd(summary.apiToSeatUsd)}/mo.` });
        }
        if (summary.licenseValueUsd > 0) {
            callouts.push({ tone: 'headline', text: `Current seats save ~${formatUsd(summary.licenseValueUsd)}/mo versus metering the same usage.` });
        }
        if (summary.lowCoverage) {
            callouts.push({ tone: 'watch', text: 'No priced usage was found, so metered-equivalents could not be computed — seat rows show their full cost as wasted.' });
        }
        return callouts;
    }

    /** Active global-filter selections for a field (user values normalized), or null when the field is unfiltered. */
    private activeValues(field: 'provider_connection_id' | 'user_id'): Set<string> | null {
        const dimension = this.filters.dimensions.find((entry) => entry.field === field);
        if (dimension === undefined || dimension.values.length === 0) return null;
        return new Set(field === 'user_id' ? dimension.values.map(normalizeUserId) : dimension.values);
    }
}
